import React, { useState } from "react";
import { api } from "../lib/api";
import { Plus } from "@phosphor-icons/react";

const Field = ({ label, children }) => (
  <div>
    <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 mb-1 block">{label}</label>
    {children}
  </div>
);

export default function NewSkuModal({ initialSku, onClose, onCreated, ctaLabel = "Create & Add", subtitle }) {
  const [form, setForm] = useState({
    sku: initialSku || "", name: "", barcode: "", category: "", hsn: "",
    gst_rate: "", mrp: "", cost_price: "", selling_price: "", master_sku: "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const inputCls = "w-full h-9 px-3 border border-slate-300 rounded-md text-sm";

  const save = async () => {
    if (!form.sku.trim() || !form.name.trim()) { setError("SKU code and product name are required."); return; }
    setSaving(true); setError("");
    try {
      const r = await api.post("/warehouse/skus", {
        sku: form.sku.trim(), name: form.name.trim(), barcode: form.barcode.trim(),
        category: form.category.trim(), hsn: form.hsn.trim(), master_sku: form.master_sku.trim(),
        gst_rate: parseFloat(form.gst_rate) || 0, mrp: parseFloat(form.mrp) || 0,
        cost_price: parseFloat(form.cost_price) || 0, selling_price: parseFloat(form.selling_price) || 0,
      });
      onCreated(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-md shadow-2xl w-full max-w-xl" onClick={(e) => e.stopPropagation()} data-testid="new-sku-modal">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="font-display font-bold text-lg">Create New SKU</div>
            <div className="text-xs text-slate-500">{subtitle || "Added to the SKU Master and to this document in one step."}</div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-md">✕</button>
        </div>
        <div className="p-5 grid grid-cols-2 gap-3">
          <Field label="SKU Code *">
            <input data-testid="new-sku-code" value={form.sku} onChange={(e) => set("sku", e.target.value)} className={`${inputCls} font-mono-num`} placeholder="e.g. SNDCBL01" />
          </Field>
          <Field label="Barcode">
            <input data-testid="new-sku-barcode" value={form.barcode} onChange={(e) => set("barcode", e.target.value)} className={`${inputCls} font-mono-num`} />
          </Field>
          <div className="col-span-2">
            <Field label="Product Name *">
              <input data-testid="new-sku-name" value={form.name} onChange={(e) => set("name", e.target.value)} className={inputCls} />
            </Field>
          </div>
          <Field label="Category">
            <input data-testid="new-sku-category" value={form.category} onChange={(e) => set("category", e.target.value)} className={inputCls} />
          </Field>
          <Field label="Master SKU (parent product)">
            <input data-testid="new-sku-master" value={form.master_sku} onChange={(e) => set("master_sku", e.target.value)} className={inputCls} placeholder="e.g. Galaxy High Chair" />
          </Field>
          <Field label="HSN">
            <input data-testid="new-sku-hsn" value={form.hsn} onChange={(e) => set("hsn", e.target.value)} className={`${inputCls} font-mono-num`} />
          </Field>
          <Field label="GST %">
            <input data-testid="new-sku-gst" type="number" step="0.1" value={form.gst_rate} onChange={(e) => set("gst_rate", e.target.value)} className={`${inputCls} font-mono-num`} placeholder="18" />
          </Field>
          <Field label="MRP (₹)">
            <input data-testid="new-sku-mrp" type="number" step="0.01" value={form.mrp} onChange={(e) => set("mrp", e.target.value)} className={`${inputCls} font-mono-num`} />
          </Field>
          <Field label="Purchase Rate (₹)">
            <input data-testid="new-sku-cost" type="number" step="0.01" value={form.cost_price} onChange={(e) => set("cost_price", e.target.value)} className={`${inputCls} font-mono-num`} />
          </Field>
          <Field label="Selling Price (₹, incl. GST)">
            <input data-testid="new-sku-price" type="number" step="0.01" value={form.selling_price} onChange={(e) => set("selling_price", e.target.value)} className={`${inputCls} font-mono-num`} />
          </Field>
          {error && <div className="col-span-2 bg-red-50 border border-red-200 text-red-700 text-xs rounded-md p-2" data-testid="new-sku-error">{error}</div>}
        </div>
        <div className="px-5 py-4 border-t border-slate-200 flex justify-end gap-2">
          <button onClick={onClose} className="h-10 px-4 text-sm font-semibold text-slate-700 hover:bg-slate-100 rounded-md">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="new-sku-save" className="h-10 px-5 bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-bold rounded-md disabled:opacity-50 inline-flex items-center gap-1.5">
            <Plus size={14} weight="bold" /> {saving ? "Creating…" : ctaLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
